import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { ContactInterface } from "interfaces/contact";
import {
  BillingInterface,
  CreditCardInterface,
  DirectDebitInterface,
  SummaryModelInterface,
} from "interfaces/summary";
import { RootState } from "app/store";
import { getConfiguration } from "app/utils";
import configuration from "components/features/Summary/configuration";

const customerConfiguration = getConfiguration(configuration);
const ccCode = customerConfiguration.paymentMethods.ccConfig.ccPaymentCode;
const ddEnabled = customerConfiguration.paymentMethods.ddConfig?.ddEnabled;

interface SummaryState {
  model: SummaryModelInterface;
  selectedPaymentMethod: string;
  bookingInProgress: boolean;
  fqtvNotValidated: boolean;
  challengeInProgress: boolean;
  couldNotFetchOpc: boolean;
}

const initialState: SummaryState = {
  model: customerConfiguration.model,
  selectedPaymentMethod: ddEnabled ? "DIRECTDEBIT" : ccCode,
  bookingInProgress: false,
  fqtvNotValidated: false,
  challengeInProgress: false,
  couldNotFetchOpc: false,
};

export const summarySlice = createSlice({
  name: "payment",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setPaymentModel: (
      state,
      action: PayloadAction<Partial<SummaryModelInterface>>,
    ) => {
      state.model = {
        ...state.model,
        ...action.payload,
      };
    },
    setCcModel: (
      state,
      action: PayloadAction<Partial<CreditCardInterface>>,
    ) => {
      state.model.cc = {
        ...state.model.cc,
        ...action.payload,
      };
    },
    setDdModel: (
      state,
      action: PayloadAction<Partial<DirectDebitInterface>>,
    ) => {
      state.model.dd = {
        ...state.model.dd,
        ...action.payload,
      };
    },
    setBillingModel: (
      state,
      action: PayloadAction<Partial<BillingInterface>>,
    ) => {
      state.model.billing = {
        ...state.model.billing,
        ...action.payload,
      };
    },
    setContactModel: (
      state,
      action: PayloadAction<Partial<ContactInterface>>,
    ) => {
      state.model.contact = {
        ...state.model.contact,
        ...action.payload,
      };
    },
    setPaymentMethod: (state, action: PayloadAction<string>) => {
      state.selectedPaymentMethod = action.payload;
    },
    setFqtvNotValidated: (state, action: PayloadAction<boolean>) => {
      state.fqtvNotValidated = action.payload;
    },
    setChallengeInProgress: (state, action: PayloadAction<boolean>) => {
      state.challengeInProgress = action.payload;
    },
    setBookingInProgress: (state, action: PayloadAction<boolean>) => {
      state.bookingInProgress = action.payload;
    },
    setCouldNotFetchOpc: (state, action: PayloadAction<boolean>) => {
      state.couldNotFetchOpc = action.payload;
    },
    resetPayment: (state) => {
      state.model = initialState.model;
      state.selectedPaymentMethod = initialState.selectedPaymentMethod;
      state.fqtvNotValidated = initialState.fqtvNotValidated;
      state.challengeInProgress = initialState.challengeInProgress;
      state.bookingInProgress = initialState.bookingInProgress;
      state.couldNotFetchOpc = initialState.couldNotFetchOpc;
    },
  },
});

export const {
  setPaymentModel,
  setCcModel,
  setDdModel,
  setBillingModel,
  resetPayment,
  setContactModel,
  setPaymentMethod,
  setFqtvNotValidated,
  setChallengeInProgress,
  setBookingInProgress,
  setCouldNotFetchOpc,
} = summarySlice.actions;

export const selectPaymentModel = (state: RootState) => state.summary.model;
export const selectPaymentMethod = (state: RootState) =>
  state.summary.selectedPaymentMethod;
export const selectFqtvNotValidated = (state: RootState) =>
  state.summary.fqtvNotValidated;
export const selectChallengeInProgress = (state: RootState) =>
  state.summary.challengeInProgress;
export const selectBookingInProgress = (state: RootState) =>
  state.summary.bookingInProgress;
export const selectCouldNotFetchOpc = (state: RootState) =>
  state.summary.couldNotFetchOpc;
export default summarySlice.reducer;
